import {createAsyncThunk, createEntityAdapter, createSlice} from "@reduxjs/toolkit";
import type {RootState} from "./store";
import type {Invoice, InvoiceBulkDownloadFilterRequest} from "../types/invoice";
import {bulkDownloadInvoices, deleteInvoice as deleteInvoiceApi, getInvoices} from "../api/invoicesApi";

type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";

const invoicesAdapter = createEntityAdapter<Invoice>({
  sortComparer: (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
});

export type InvoicesState = ReturnType<typeof invoicesAdapter.getInitialState> & {
  status: AsyncStatus;
  error: string | null;
  deleteStatus: AsyncStatus;
  bulkDownloadStatus: AsyncStatus;
};

const initialState: InvoicesState = invoicesAdapter.getInitialState({
  status: "idle",
  error: null,
  deleteStatus: "idle",
  bulkDownloadStatus: "idle",
});

export const fetchInvoices = createAsyncThunk<Invoice[], void | { includeDeleted?: boolean }>(
  "invoices/fetchInvoices",
  async (arg) => {
    return getInvoices(typeof arg === "object" ? arg : undefined);
  },
);

export const deleteInvoice = createAsyncThunk<string, string>(
  "invoices/deleteInvoice",
  async (id) => {
    await deleteInvoiceApi(id);
    return id;
  },
);

export const bulkDownloadInvoicesThunk = createAsyncThunk<
  { presignedDownloadUrl: string },
  InvoiceBulkDownloadFilterRequest
>("invoices/bulkDownload", async (request) => {
  return bulkDownloadInvoices(request);
});

export const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    reset(state) {
      invoicesAdapter.removeAll(state);
      state.status = "idle";
      state.error = null;
      state.deleteStatus = "idle";
      state.bulkDownloadStatus = "idle";
    },
    removeInvoiceFromCache(state, action: { payload: string }) {
      invoicesAdapter.removeOne(state, action.payload);
    },
    upsertInvoice(state, action: { payload: Invoice }) {
      invoicesAdapter.upsertOne(state, action.payload);
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.status = "succeeded";
        invoicesAdapter.setAll(state, action.payload);
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load invoices";
      })
      .addCase(deleteInvoice.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        invoicesAdapter.removeOne(state, action.payload);
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.error.message ?? "Failed to delete invoice";
      })
      .addCase(bulkDownloadInvoicesThunk.pending, (state) => {
        state.bulkDownloadStatus = "loading";
        state.error = null;
      })
      .addCase(bulkDownloadInvoicesThunk.fulfilled, (state) => {
        state.bulkDownloadStatus = "succeeded";
      })
      .addCase(bulkDownloadInvoicesThunk.rejected, (state, action) => {
        state.bulkDownloadStatus = "failed";
        state.error = action.error.message ?? "Bulk download failed";
      });
  },
});

export const {reset, removeInvoiceFromCache, upsertInvoice} = invoicesSlice.actions;
export const invoicesSelectors = invoicesAdapter.getSelectors<RootState>((s) => s.invoices);
